import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  FileText,
  Shield,
  Package,
  Activity,
  Database,
  Gauge,
  Thermometer,
  Wrench,
  QrCode,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowLeft,
  Download,
  FileDown,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoBanner from '../../components/optimar/DemoBanner';
import SectionBreak from '../../components/optimar/SectionBreak';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_ID = 'OPT-DEMO-001';

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-NO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
  <section
    className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
    data-testid={testId}
  >
    <div className="mb-4 flex items-start gap-3 border-b border-slate-100 pb-3">
      <div className="rounded-lg bg-slate-100 p-2 shrink-0">
        <Icon className="h-5 w-5 text-slate-700" />
      </div>
      <div className="min-w-0">
        <h2
          className="text-base font-semibold text-slate-900 sm:text-lg"
          style={{ fontFamily: 'Manrope' }}
        >
          {title}
        </h2>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-1.5 text-sm last:border-b-0">
    <span className="text-slate-500">{label}</span>
    <span
      className={`text-right text-slate-900 ${mono ? 'font-mono' : 'font-medium'}`}
    >
      {value ?? '—'}
    </span>
  </div>
);

const StatusPill = ({ status }) => {
  const styles = {
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
    missing: 'bg-amber-100 text-amber-800 border-amber-300',
    skipped: 'bg-slate-100 text-slate-600 border-slate-200',
    resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    open: 'bg-amber-100 text-amber-800 border-amber-300',
    compliant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  const cls = styles[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  const label = String(status || '').replace(/_/g, ' ');
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
    >
      {label}
    </span>
  );
};

const OptimarComplianceReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/compliance-report`
      );
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [exporting, setExporting] = useState(null);

  const downloadExport = async (kind) => {
    try {
      setExporting(kind);
      const ext = kind === 'pdf' ? 'pdf' : 'json';
      const url = `${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/export.${ext}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: kind === 'pdf' ? 'application/pdf' : 'application/json',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${BATCH_ID}-documentation-export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(
        `Demo ${kind.toUpperCase()} export downloaded — not final regulatory documentation.`
      );
    } catch (err) {
      console.error(err);
      toast.error(`Failed to download ${kind.toUpperCase()} export`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-slate-600">Loading compliance report…</p>
        </div>
      </div>
    );
  }
  if (!report) return null;

  const {
    batch_identity: bi,
    traceability_status: ts,
    production_history: history,
    data_sources: ds,
    quality_and_yield: qy,
    haccp,
    manual_correction_log: corrections,
    compliance_items: complianceItems,
    customer_summary: cs,
    methodology,
  } = report;

  return (
    <div className="space-y-10 sm:space-y-12" data-testid="optimar-compliance-report-page">
      <DemoBanner />

      {/* Preview header */}
      <div
        data-testid="optimar-report-preview-header"
        className="rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="rounded-lg bg-white p-3 shrink-0">
              <FileText className="h-6 w-6 text-blue-700" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider text-blue-700">
                Demo compliance report preview
              </p>
              <h1
                className="text-xl font-bold leading-tight text-slate-900 sm:text-2xl break-words"
                style={{ fontFamily: 'Manrope' }}
              >
                Batch {bi.batch_id} — {bi.product_name}
              </h1>
              <p className="mt-1 max-w-xl text-xs text-slate-600">
                {report.preview_notice}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Generated at {formatDate(report.generated_at)}
              </p>
              <p
                className="mt-2 text-xs text-slate-600"
                data-testid="optimar-report-platform-attribution"
              >
                <span className="font-semibold text-slate-800">PureSustain Platform</span>{' '}
                © <span className="font-semibold text-slate-800">PureSustain AS</span>{' '}
                · under{' '}
                <span className="font-semibold text-slate-800">VeraBlue Capital AS</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="optimar-report-audit-ready"
              className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium sm:text-sm ${
                report.audit_ready
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}
            >
              {report.audit_ready ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              {report.audit_ready ? 'Audit-ready' : 'Action required'}
            </span>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <Link
            to="/optimar/batch"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800"
            data-testid="optimar-report-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to batch
          </Link>
          <div
            className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center"
            data-testid="optimar-report-export-bar"
          >
            <span
              className="hidden text-xs text-slate-500 sm:inline"
              data-testid="optimar-report-export-notice"
            >
              Demo export — not final regulatory documentation.
            </span>
            <button
              type="button"
              onClick={() => downloadExport('json')}
              disabled={exporting !== null}
              data-testid="optimar-report-export-json-btn"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:py-1.5"
            >
              <FileDown className="h-4 w-4" />
              {exporting === 'json' ? 'Preparing…' : 'Download JSON'}
            </button>
            <button
              type="button"
              onClick={() => downloadExport('pdf')}
              disabled={exporting !== null}
              data-testid="optimar-report-export-pdf-btn"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:py-1.5"
            >
              <Download className="h-4 w-4" />
              {exporting === 'pdf' ? 'Preparing…' : 'Download PDF'}
            </button>
            <a
              href={`/verify/${BATCH_ID}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="optimar-report-open-public-qr-btn"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 sm:w-auto sm:py-1.5"
            >
              <ExternalLink className="h-4 w-4" />
              Open Public QR View
            </a>
            <span
              className="text-[10px] leading-snug text-slate-500 sm:hidden"
              data-testid="optimar-report-export-notice-mobile"
            >
              Demo export — not final regulatory documentation.
            </span>
          </div>
        </div>
      </div>

      {/* Section 1: Batch identity */}
      <Section
        icon={Package}
        title="Batch identity"
        subtitle="Identity fields for this production batch"
        testId="optimar-report-section-identity"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
          <Row label="Batch ID" value={bi.batch_id} mono />
          <Row label="Product" value={bi.product_name} />
          <Row label="Species" value={<em>{bi.species}</em>} />
          <Row label="Production line" value={bi.production_line} />
          <Row label="Production date" value={formatDate(bi.production_date)} />
          <Row label="Storage location" value={bi.storage_location} />
          <Row label="Current status" value={bi.current_status} />
          <Row label="Origin" value={bi.origin} />
        </div>
      </Section>

      {/* Section 2: Traceability status */}
      <Section
        icon={Shield}
        title="Traceability status"
        subtitle="Confidence, chain-of-custody completeness and warning history"
        testId="optimar-report-section-traceability"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Confidence score
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900">
              {ts.confidence_score}%
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Chain-of-custody (auto)
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900">
              {ts.chain_of_custody_auto_pct}%
            </p>
            <p className="text-[11px] text-slate-500">
              {ts.steps_auto_recorded}/{ts.steps_total} SCADA-recorded
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              With manual closures
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900">
              {ts.chain_of_custody_with_manual_pct}%
            </p>
            <p className="text-[11px] text-slate-500">
              {ts.steps_closed_via_manual_correction} step(s) manually closed
            </p>
          </div>
        </div>

        <Row label="Active warnings" value={ts.active_warnings} />
        <Row label="Manual correction status" value={ts.manual_correction_status} />

        <div>
          <p className="mb-2 mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Warning history
          </p>
          {ts.warning_history.length === 0 ? (
            <p className="text-sm text-slate-500">No warnings recorded.</p>
          ) : (
            <ul className="space-y-2">
              {ts.warning_history.map((w) => (
                <li
                  key={w.id}
                  data-testid={`optimar-report-warning-${w.id}`}
                  className={`rounded-lg border p-3 ${
                    w.status === 'resolved'
                      ? 'border-emerald-200 bg-emerald-50/40'
                      : 'border-amber-300 bg-amber-50/40'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {w.title}
                    </p>
                    <StatusPill status={w.status} />
                  </div>
                  <div className="mt-1 grid grid-cols-1 gap-1 text-xs text-slate-600 sm:grid-cols-2">
                    <span>
                      <strong>Raised:</strong> {formatDate(w.raised_at)}
                    </span>
                    {w.gap_between && (
                      <span>
                        <strong>Gap:</strong> {w.gap_between.join(' → ')}
                      </span>
                    )}
                    {w.resolved_at && (
                      <span>
                        <strong>Resolved:</strong> {formatDate(w.resolved_at)}
                      </span>
                    )}
                    {w.resolved_by && (
                      <span>
                        <strong>By:</strong> {w.resolved_by}
                      </span>
                    )}
                    {w.correction_id && (
                      <span className="font-mono sm:col-span-2">
                        <strong>Correction:</strong> {w.correction_id}
                      </span>
                    )}
                    {w.temperature_celsius != null && (
                      <span data-testid={`optimar-report-warning-${w.id}-temperature`}>
                        <strong>Temperature recorded:</strong>{' '}
                        {Number(w.temperature_celsius).toFixed(1)} °C
                      </span>
                    )}
                    {w.resolution_reason && (
                      <span className="sm:col-span-2">
                        <strong>Reason:</strong> {w.resolution_reason}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      {/* Section 3: Production history */}
      <Section
        icon={Activity}
        title="Production history"
        subtitle="Timestamped events from intake to cold storage"
        testId="optimar-report-section-history"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Stage</th>
                <th className="px-2 py-2 text-left">Timestamp</th>
                <th className="px-2 py-2 text-left">Location</th>
                <th className="px-2 py-2 text-left">Temp °C</th>
                <th className="px-2 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr
                  key={h.step_number}
                  data-testid={`optimar-report-history-${h.stage_key}`}
                  className="border-b border-slate-100 last:border-b-0"
                >
                  <td className="px-2 py-2 font-mono text-slate-500">
                    {String(h.step_number).padStart(2, '0')}
                  </td>
                  <td className="px-2 py-2 font-medium text-slate-900">
                    {h.stage_label}
                  </td>
                  <td className="px-2 py-2 text-slate-600">
                    {formatDate(h.timestamp)}
                  </td>
                  <td className="px-2 py-2 text-slate-600">{h.location || '—'}</td>
                  <td className="px-2 py-2 text-slate-600">
                    {h.temperature_celsius ?? '—'}
                  </td>
                  <td className="px-2 py-2">
                    <StatusPill status={h.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Section 4: Data sources */}
      <Section
        icon={Database}
        title="Data sources"
        subtitle="SCADA / PostgreSQL demo sources, RFID, QR, carton & pallet IDs"
        testId="optimar-report-section-sources"
      >
        <Row label="SCADA demo source" value={ds.scada_demo_source} mono />
        <Row label="PostgreSQL demo source" value={ds.postgres_demo_source} mono />
        <Row label="RFID tag range" value={ds.rfid_tag_range} mono />
        <Row label="QR label range" value={ds.qr_label_range} mono />
        <Row label="Carton ID range" value={ds.carton_id_range} mono />
        <Row label="Pallet ID range" value={ds.pallet_id_range} mono />
        <div>
          <p className="mb-2 mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            PostgreSQL tables referenced
          </p>
          <div className="flex flex-wrap gap-2">
            {ds.postgres_tables.map((t) => (
              <span
                key={t}
                className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* Section 5: Quality & yield */}
      <Section
        icon={Gauge}
        title="Quality and yield metrics"
        testId="optimar-report-section-quality"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
          <Row label="Raw weight" value={`${qy.raw_weight_kg.toLocaleString()} kg`} />
          <Row
            label="Processed weight"
            value={`${qy.processed_weight_kg.toLocaleString()} kg`}
          />
          <Row label="Yield percentage" value={`${qy.yield_percentage}%`} />
          <Row
            label="Discarded product weight"
            value={`${qy.discarded_weight_kg.toLocaleString()} kg`}
          />
          <Row label="Quality grade" value={qy.quality_grade} />
          <Row label="Size class" value={qy.size_class} />
          <Row
            label="Processing time"
            value={`${qy.processing_time_minutes} min`}
          />
        </div>
      </Section>

      {/* Section 6: HACCP */}
      <Section
        icon={Thermometer}
        title="HACCP-style temperature control"
        subtitle="Per-stage readings vs. demo thresholds, plus deviations"
        testId="optimar-report-section-haccp"
      >
        <div className="flex items-center gap-2">
          <span
            data-testid="optimar-report-haccp-overall"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
              haccp.all_within_threshold
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : 'border-rose-300 bg-rose-50 text-rose-800'
            }`}
          >
            {haccp.all_within_threshold ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5" />
            )}
            {haccp.all_within_threshold
              ? 'All readings within threshold'
              : `${haccp.deviations.length} deviation(s) detected`}
          </span>
          <span className="text-xs text-slate-500">
            Cold storage:{' '}
            <strong>
              {haccp.cold_storage_status.temperature_celsius ?? '—'} °C
            </strong>{' '}
            ({haccp.cold_storage_status.status})
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-2 py-2 text-left">Stage</th>
                <th className="px-2 py-2 text-left">Timestamp</th>
                <th className="px-2 py-2 text-left">Temp °C</th>
                <th className="px-2 py-2 text-left">Threshold</th>
                <th className="px-2 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {haccp.readings.map((r) => (
                <tr
                  key={r.stage_key}
                  data-testid={`optimar-report-haccp-${r.stage_key}`}
                  className="border-b border-slate-100 last:border-b-0"
                >
                  <td className="px-2 py-2 font-medium text-slate-900">
                    {r.stage_label}
                  </td>
                  <td className="px-2 py-2 text-slate-600">
                    {formatDate(r.timestamp)}
                  </td>
                  <td className="px-2 py-2 text-slate-600">
                    {r.temperature_celsius ?? '—'}
                  </td>
                  <td className="px-2 py-2 text-slate-600">
                    ≤ {r.threshold_max_celsius ?? '—'} °C
                  </td>
                  <td className="px-2 py-2">
                    {r.missing_reading ? (
                      <StatusPill status="missing" />
                    ) : r.within_threshold ? (
                      <StatusPill status="compliant" />
                    ) : (
                      <StatusPill status="open" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="rounded-lg bg-slate-50 p-3 text-xs italic text-slate-600">
          {haccp.thresholds_note}
        </p>
      </Section>

      {/* Section 7: Manual correction log */}
      <Section
        icon={Wrench}
        title="Manual correction log"
        subtitle="Operator-recorded corrections with before/after status"
        testId="optimar-report-section-corrections"
      >
        {corrections.length === 0 ? (
          <p className="text-sm text-slate-500">
            No manual corrections recorded for this batch.
          </p>
        ) : (
          <ul className="space-y-3">
            {corrections.map((c) => (
              <li
                key={c.id}
                data-testid={`optimar-report-correction-${c.id}`}
                className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-emerald-800">
                    {c.id}
                  </span>
                  <span className="text-xs text-slate-500">
                    {formatDate(c.timestamp)}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                  <Row label="Affected production step" value={c.related_stage} />
                  <Row label="Corrected by" value={c.operator} />
                  <Row label="Before status" value={c.before_status} />
                  <Row label="After status" value={c.after_status} />
                  {c.temperature_celsius != null && (
                    <Row
                      label="Temperature recorded"
                      value={`${Number(c.temperature_celsius).toFixed(1)} °C`}
                    />
                  )}
                </div>
                <p className="mt-1 text-sm text-slate-700">
                  <strong>Reason:</strong> {c.reason}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Section 8: Customer summary */}
      <Section
        icon={QrCode}
        title="Customer-facing summary"
        subtitle="Limited public info exposed via the QR / verify page"
        testId="optimar-report-section-customer"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
          <Row label="Product" value={cs.product} />
          <Row label="Origin" value={cs.origin} />
          <Row label="Quality grade" value={cs.quality_grade} />
          <Row label="Production date" value={formatDate(cs.production_date)} />
          <Row label="Storage status" value={cs.storage_status} />
          <Row label="Traceability status" value={cs.traceability_status} />
          <Row
            label="Sustainability badges"
            value={cs.sustainability_badges.join(' · ')}
          />
          <Row label="Public verify path" value={cs.verify_url_path} mono />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={cs.verify_url_path}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 hover:bg-blue-100"
            data-testid="optimar-report-open-public-verify"
          >
            Open public verify page
          </Link>
          <Link
            to="/optimar/customer"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="optimar-report-open-customer-view"
          >
            Open internal customer / QR view
          </Link>
        </div>
      </Section>

      {/* Compliance items footer */}
      <Section
        icon={Info}
        title="Compliance items (overview)"
        testId="optimar-report-section-items"
      >
        {complianceItems.map((c, idx) => (
          <div
            key={idx}
            className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">{c.standard}</p>
              <p className="mt-1 text-xs text-slate-500">{c.description}</p>
            </div>
            <StatusPill status={c.status} />
          </div>
        ))}
      </Section>

      {/* Demo Score Methodology — collapsible by default */}
      {methodology && (
        <>
          <SectionBreak data-testid="optimar-report-break-before-methodology" />
          <section
            className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 sm:p-6"
            data-testid="optimar-report-section-methodology"
          >
          <details className="group">
            <summary
              className="flex cursor-pointer list-none items-start justify-between gap-3"
              data-testid="optimar-report-methodology-toggle"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="rounded-lg bg-amber-100 p-2 shrink-0">
                  <Info className="h-5 w-5 text-amber-700" />
                </div>
                <div className="min-w-0">
                  <h2
                    className="text-base font-semibold text-slate-900 sm:text-lg"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    {methodology.title || 'Demo Score Methodology'}
                  </h2>
                  <p className="text-xs font-medium uppercase tracking-wider text-amber-800">
                    {methodology.label || 'Illustrative future methodology only'}
                  </p>
                </div>
              </div>
              <span
                className="shrink-0 select-none rounded-full border border-amber-300 bg-white px-3 py-1 text-[11px] font-medium text-amber-800 group-open:hidden"
                aria-hidden="true"
              >
                Show
              </span>
              <span
                className="hidden shrink-0 select-none rounded-full border border-amber-300 bg-white px-3 py-1 text-[11px] font-medium text-amber-800 group-open:inline-block"
                aria-hidden="true"
              >
                Hide
              </span>
            </summary>

            <div
              className="mt-4 space-y-4"
              data-testid="optimar-report-methodology-body"
            >
              {methodology.notice && (
                <p className="text-sm leading-relaxed text-slate-700">
                  {methodology.notice}
                </p>
              )}

              <ul className="space-y-3">
                {(methodology.indicators || []).map((ind) => (
                  <li
                    key={ind.indicator}
                    data-testid={`optimar-methodology-${ind.indicator
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')}`}
                    className="rounded-lg border border-amber-200 bg-white p-3"
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {ind.indicator}
                    </p>
                    {ind.inputs && ind.inputs.length > 0 && (
                      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-slate-600">
                        {ind.inputs.map((i) => (
                          <li key={i}>{i}</li>
                        ))}
                      </ul>
                    )}
                    {ind.example_formula && (
                      <p className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-xs italic text-slate-700">
                        <span className="font-semibold not-italic text-slate-800">
                          Example:{' '}
                        </span>
                        {ind.example_formula}
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              {methodology.disclaimer && (
                <p
                  className="text-xs italic text-amber-900"
                  data-testid="optimar-report-methodology-disclaimer"
                >
                  {methodology.disclaimer}
                </p>
              )}
            </div>
          </details>
          </section>
        </>
      )}

      <p className="pt-2 text-center text-xs text-slate-400">
        Demo compliance report preview — {report.data_label}. Not a regulatory
        certification.
      </p>
    </div>
  );
};

export default OptimarComplianceReport;
